import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Palette, FileText, Menu, X } from 'lucide-react';

const NavLink = ({ to, icon: Icon, label, external, color, onClick }) => {
  // Convert color classes to background colors
  const getBlobColor = (color) => {
    const colorMap = {
      'text-emerald-500': 'bg-emerald-100/80',
      'text-blue-500': 'bg-blue-100/80',
      'text-violet-500': 'bg-violet-100/80'
    };
    return colorMap[color] || 'bg-gray-100';
  };

  const baseClasses = "flex items-center gap-3 px-4 py-2 rounded-full transition-all duration-300 hover:bg-gray-50 w-full md:w-auto group";
  
  const iconContainer = `
    relative w-10 h-10 flex items-center justify-center
    ${getBlobColor(color)} rounded-full
    transition-transform duration-300 group-hover:scale-105
  `;

  if (external) {
    return (
      <a 
        href={to}
        target="_blank" 
        rel="noopener noreferrer"
        className={`${baseClasses}`}
        onClick={onClick}
      >
        <div className={iconContainer}>
          <Icon size={18} className="text-black" />
        </div>
        <span className="text-gray-700 font-medium">{label}</span>
      </a>
    );
  }

  return (
    <Link 
      to={to} 
      className={baseClasses}
      onClick={onClick}
    >
      <div className={iconContainer}>
        <Icon size={18} className="text-black" />
      </div>
      <span className="text-gray-700 font-medium">{label}</span>
    </Link>
  );
};

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  const closeMenu = () => setIsOpen(false);

  return (
    <nav className="relative">
      {/* Desktop and Mobile Header */}
      <div className="py-6 flex justify-between items-center">
        <Link to="/" className="text-red-500 font-fraunces text-2xl">दीपाली</Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex gap-2">
          <NavLink 
            to="/shop" 
            icon={ShoppingBag} 
            label="Shop" 
            color="text-emerald-500"
          />
          <NavLink 
            to="https://www.behance.net/ceilkey" 
            icon={Palette} 
            label="Work" 
            external 
            color="text-blue-500"
          />
          <NavLink 
            to="https://drive.google.com/file/d/1VPHkLiZMqp-ueWbQJUgurIBsv3SnYCkd/view?usp=sharing" 
            icon={FileText} 
            label="Resume" 
            external 
            color="text-violet-500"
          />
        </div>

        {/* Mobile Menu Button */}
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <X size={24} className="text-gray-700" />
          ) : (
            <Menu size={24} className="text-gray-700" />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`
          fixed md:hidden inset-0 bg-white z-50 transition-all duration-300 ease-in-out
          ${isOpen 
            ? 'opacity-100 translate-x-0' 
            : 'opacity-0 translate-x-full pointer-events-none'
          }
        `}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="py-6 px-4 border-b flex justify-between items-center">
            <Link 
              to="/" 
              className="text-red-500 font-fraunces text-2xl"
              onClick={closeMenu}
            >
              दीपाली
            </Link>
            <button 
              onClick={closeMenu}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close menu"
            >
              <X size={24} className="text-gray-700" />
            </button>
          </div>

          {/* Mobile Menu Links */}
          <div className="flex flex-col gap-3 p-4">
            <NavLink 
              to="/shop" 
              icon={ShoppingBag} 
              label="Shop" 
              color="text-emerald-500"
              onClick={closeMenu}
            />
            <NavLink 
              to="https://www.behance.net/ceilkey" 
              icon={Palette} 
              label="Work" 
              external 
              color="text-blue-500"
              onClick={closeMenu}
            />
            <NavLink 
              to="https://drive.google.com/file/d/1VPHkLiZMqp-ueWbQJUgurIBsv3SnYCkd/view?usp=sharing" 
              icon={FileText} 
              label="Resume" 
              external 
              color="text-violet-500"
              onClick={closeMenu}
            />
          </div>

          {/* Bottom Section */}
          <div className="mt-auto p-4 border-t text-center text-sm text-gray-500">
            <p>© 2024 Deepali Agrawal</p>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;